import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import {
  CHAT_STORE_VERSION,
  computeCrossTabMerge,
  createCrossTabChatStorage,
  filterToBusiness,
  finalizeInterruptedStreams,
  parseChatBlob,
  type PersistedAIChatState,
} from '@/stores/aiChatCrossTab'
import { insertBeforeUnsentTail } from '@/stores/aiChatMessageOrdering'
import { applyRestoredMessages, generateTitle } from '@/stores/aiChatRestore'
import type { AIChatMessage, AIConversation } from '@/stores/aiChatTypes'
import { buildWelcomeMessage } from '@/stores/aiChatWelcome'
import { PERSISTED_STORE_KEYS, registerPersistedStore } from '@/stores/persistedKeys'
import { useAuthStore } from '@/stores/useAuthStore'

// AI Chat store state interface
interface AIChatState {
  conversations: AIConversation[]
  activeConversationId: string | null
  /** Tombstones for deliberate deletes (#2208) — see aiChatCrossTab.ts. */
  deletedConversationIds: string[]
  searchQuery: string
  isLoading: boolean
  error: string | null
}

// AI Chat store actions interface
interface AIChatActions {
  // Conversation actions
  createConversation: () => string
  selectConversation: (id: string | null) => void

  // Message actions
  /**
   * Append a message and return its generated id — callers that need to
   * track this specific bubble's delivery (#2281: a user message's
   * pending/sent/failed status) hold onto the id rather than re-deriving
   * "whatever is now last" the moment other appends can race it (#2255).
   *
   * `options.insertBeforeUnsentTail` anchors the message just ahead of any
   * trailing user messages that haven't reached the server yet, instead of
   * the tail — for a reply/question delivered through the resume or
   * notification channel, which always resolves an EARLIER turn than one
   * still sitting unsent at the end of the transcript.
   */
  addMessage: (
    conversationId: string,
    message: Omit<AIChatMessage, 'id' | 'timestamp'>,
    options?: { insertBeforeUnsentTail?: boolean }
  ) => string
  /**
   * Replace (or, with `prepend`, extend backwards) a conversation's messages
   * with a page restored from the backend transcript, in one set(). Restored
   * messages keep their server ids and created_at timestamps — addMessage
   * self-stamps the clock, which is right for live sends and wrong for
   * restore (#2277).
   */
  restoreMessages: (
    conversationId: string,
    messages: AIChatMessage[],
    options?: { prepend?: boolean }
  ) => void
  updateMessage: (
    conversationId: string,
    messageId: string,
    updates: Partial<AIChatMessage>
  ) => void
  removeMessage: (conversationId: string, messageId: string) => void

  // Search
  setSearchQuery: (query: string) => void

  // Clear current conversation
  clearConversation: () => void

  // Backend-connected actions
  // NOTE: sending is owned by useHermesChat (send). Earlier we had a
  // duplicate sendMessageToBackend/cancelStream path here which ran in
  // parallel with the transport hook and produced phantom "typing"
  // placeholders + occasional role-flipped bubbles. Deleted; if you need
  // to send programmatically use useHermesChat.send.
  clearError: () => void
}

// Combined store type
type AIChatStore = AIChatState & AIChatActions

// Initial state
const initialState: AIChatState = {
  conversations: [],
  activeConversationId: null,
  deletedConversationIds: [],
  searchQuery: '',
  isLoading: false,
  error: null,
}

// Everything scoped to "the conversation on screen" — reset together at every
// lifecycle boundary (new / switch / clear / delete-active). Spread this in
// each lifecycle action instead of hand-listing fields, so the next ephemeral
// field added here can't be forgotten in three of four places (#1993 leaked a
// stale Schedule Post card into fresh conversations exactly that way; that
// card now lives on message metadata, #2201).
const conversationEphemeralReset: Pick<AIChatState, 'error'> = {
  error: null,
}

// Helper to generate IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

/**
 * Business the app is currently acting as, read from the cached auth user —
 * the same source `api/agent.ts` uses for the `X-Business-ID` header, so a
 * conversation is always stamped with the tenant its requests were sent to.
 * `null` before `/auth/me` resolves.
 */
const currentBusinessId = (): string | null => {
  const user = useAuthStore.getState().user as Record<string, unknown> | null
  return (user?.default_business_id as string | undefined) ?? null
}

// Create the AI chat store with devtools and persist middleware
export const useAIChatStore = create<AIChatStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Create a new conversation
        createConversation: () => {
          const now = new Date().toISOString()
          const newId = generateId()

          const newConversation: AIConversation = {
            id: newId,
            title: 'New Conversation',
            messages: [buildWelcomeMessage(now)],
            createdAt: now,
            updatedAt: now,
            businessId: currentBusinessId(),
          }

          set((state) => ({
            conversations: [newConversation, ...state.conversations],
            activeConversationId: newId,
            ...conversationEphemeralReset,
          }))

          return newId
        },

        // Select a conversation
        selectConversation: (id: string | null) => {
          set({
            activeConversationId: id,
            ...conversationEphemeralReset,
          })
        },

        // Add a message to a conversation
        addMessage: (conversationId, message, options) => {
          const now = new Date().toISOString()
          const newMessage: AIChatMessage = {
            ...message,
            id: generateId(),
            timestamp: now,
            // Only user turns carry a delivery state — a caller retrying an
            // existing bubble passes its own `status` through `updateMessage`,
            // never through a second `addMessage`.
            ...(message.role === 'user' ? { status: message.status ?? 'pending' } : {}),
          }

          set((state) => {
            const conversations = state.conversations.map((c) => {
              if (c.id !== conversationId) return c

              const updatedMessages = options?.insertBeforeUnsentTail
                ? insertBeforeUnsentTail(c.messages, newMessage)
                : [...c.messages, newMessage]

              let title = c.title
              if (title === 'New Conversation' && message.role === 'user' && !message.isStreaming) {
                title = generateTitle(message.content)
              }

              return {
                ...c,
                messages: updatedMessages,
                title,
                updatedAt: now,
              }
            })

            return { conversations }
          })

          return newMessage.id
        },

        // Restore a transcript page from the server (#2277)
        restoreMessages: (conversationId, messages, options) => {
          set((state) => ({
            conversations: state.conversations.map((c) =>
              c.id === conversationId
                ? applyRestoredMessages(c, messages, options?.prepend === true)
                : c
            ),
          }))
        },

        // Update an existing message (for streaming completion)
        updateMessage: (
          conversationId: string,
          messageId: string,
          updates: Partial<AIChatMessage>
        ) => {
          set((state) => ({
            conversations: state.conversations.map((c) => {
              if (c.id !== conversationId) return c

              return {
                ...c,
                messages: c.messages.map((m) => (m.id === messageId ? { ...m, ...updates } : m)),
                updatedAt: new Date().toISOString(),
              }
            }),
          }))
        },

        // Drop one message (a reply placeholder another bubble's reply answered)
        removeMessage: (conversationId: string, messageId: string) => {
          set((state) => ({
            conversations: state.conversations.map((c) =>
              c.id === conversationId
                ? { ...c, messages: c.messages.filter((m) => m.id !== messageId) }
                : c
            ),
          }))
        },

        // Clear the active conversation (reset messages, backend session)
        clearConversation: () => {
          const state = get()
          const conversationId = state.activeConversationId
          if (!conversationId) return

          const now = new Date().toISOString()

          set((state) => ({
            conversations: state.conversations.map((c) =>
              c.id === conversationId
                ? { ...c, messages: [buildWelcomeMessage(now)], updatedAt: now }
                : c
            ),
            ...conversationEphemeralReset,
          }))
        },

        // Set search query
        setSearchQuery: (query: string) => {
          set({ searchQuery: query })
        },

        // Clear error state
        clearError: () => {
          set({ error: null })
        },
      }),
      {
        name: PERSISTED_STORE_KEYS.aiChat,
        // Merge-then-write (#2208): setItem unions the array some other tab
        // may have written since this tab last looked, so writing our own copy
        // can never drop conversation ids this tab never had.
        storage: createCrossTabChatStorage(currentBusinessId),
        // v2 introduced `businessId`. A v1 blob predates the stamp, so there is
        // no way to tell which tenant wrote it — and it is exactly the blob that
        // leaked across accounts (#1923). Discard those conversations rather
        // than guess; the alternative is re-rendering another account's message
        // text. Returning a well-formed state (instead of throwing) is what
        // keeps an old blob from crashing the store on rehydrate.
        version: CHAT_STORE_VERSION,
        migrate: (persisted) => {
          const state = (persisted ?? {}) as Partial<PersistedAIChatState>
          return {
            ...state,
            conversations: [],
            activeConversationId: null,
            deletedConversationIds: [],
          }
        },
        // Defence in depth for the case no logout ever runs: the browser is
        // closed and reopened by a different account. Login clears the store,
        // but a rehydrate happens first — so drop anything stamped with a
        // business other than the active one on the way in. A null active
        // business means auth has not hydrated yet and nothing can be judged.
        merge: (persisted, current) => {
          const incoming = (persisted ?? {}) as Partial<PersistedAIChatState>
          const activeBusinessId = currentBusinessId()
          const deletedConversationIds = incoming.deletedConversationIds ?? []
          const deleted = new Set(deletedConversationIds)
          // A restored message can never still be streaming (#2278) — a stale
          // persisted `isStreaming: true` flag would abort every resume watch
          // on the conversation forever.
          const conversations = finalizeInterruptedStreams(
            filterToBusiness(incoming.conversations ?? [], activeBusinessId)
              // A hand-written or mid-merge blob can carry a tombstoned row;
              // rehydrate is a merge path too, so the same rules apply.
              .filter((c) => !deleted.has(c.id))
          )
          const activeConversationId = conversations.some(
            (c) => c.id === incoming.activeConversationId
          )
            ? (incoming.activeConversationId ?? null)
            : null
          return {
            ...current,
            ...incoming,
            conversations,
            activeConversationId,
            deletedConversationIds,
          }
        },
        partialize: (state) => ({
          conversations: state.conversations,
          activeConversationId: state.activeConversationId,
          deletedConversationIds: state.deletedConversationIds,
        }),
      }
    ),
    { name: 'AIChatStore', enabled: import.meta.env.DEV }
  )
)

registerPersistedStore(PERSISTED_STORE_KEYS.aiChat, () => useAIChatStore.setState(initialState))

/**
 * Cross-tab convergence (#2208). `storage` fires in every OTHER tab when one
 * tab writes `ai-chat-storage`; merge the incoming copy into live state instead
 * of letting the tabs drift until one of them clobbers the other. Deliberately
 * NOT adopting the writer's `activeConversationId` — each tab keeps its own
 * open conversation; only the conversation DATA converges.
 */
const handleCrossTabChatWrite = (event: StorageEvent): void => {
  // newValue === null is the key being removed — that is logout (#1923), whose
  // tenant hygiene is owned by clearPersistedStores, not a merge.
  if (event.key !== PERSISTED_STORE_KEYS.aiChat || event.newValue === null) return
  const state = useAIChatStore.getState()
  const delta = computeCrossTabMerge(state, parseChatBlob(event.newValue), currentBusinessId())
  if (!delta) return
  useAIChatStore.setState({
    ...delta,
    // The merge orphaned this tab's active conversation — reset ephemera the
    // same way any other conversation-lifecycle boundary does.
    ...('activeConversationId' in delta ? conversationEphemeralReset : {}),
  })
}

// In the app this module loads exactly once. Under vitest, `vi.resetModules`
// re-imports it per test — a stale copy's listener would merge events into a
// dead store instance and persist that stale state back over localStorage, so
// each import evicts the previous listener via a window slot.
if (typeof window !== 'undefined') {
  const slot = window as unknown as { __aiChatCrossTabSync?: (e: StorageEvent) => void }
  if (slot.__aiChatCrossTabSync) window.removeEventListener('storage', slot.__aiChatCrossTabSync)
  slot.__aiChatCrossTabSync = handleCrossTabChatWrite
  window.addEventListener('storage', handleCrossTabChatWrite)
}

// Selectors
export const selectConversations = (state: AIChatStore) => state.conversations

export const selectActiveConversationId = (state: AIChatStore) => state.activeConversationId

export const selectActiveConversation = (state: AIChatStore) =>
  state.conversations.find((c) => c.id === state.activeConversationId) || null

export const selectSearchQuery = (state: AIChatStore) => state.searchQuery

export const selectFilteredConversations = (state: AIChatStore) => {
  const query = state.searchQuery.toLowerCase().trim()
  if (!query) return state.conversations

  return state.conversations.filter((c) => {
    if (c.title.toLowerCase().includes(query)) return true
    return c.messages.some((m) => m.content.toLowerCase().includes(query))
  })
}

export const selectActiveMessages = (state: AIChatStore) => {
  const conversation = state.conversations.find((c) => c.id === state.activeConversationId)
  return conversation?.messages || []
}

export const selectIsLoading = (state: AIChatStore) => state.isLoading

export const selectError = (state: AIChatStore) => state.error
